/**
 * StudioShow — the new primary "Show mode" screen.
 *
 * Layout:
 *   TOP       : ShowHeader (status / topic / next speaker / nav)
 *   LEFT COL  : EpisodeOverview + PanelOrder + CrewStrip
 *   CENTER    : PanelGrid (Nova/Orion/Vega/Atlas cards, focusable)
 *   RIGHT     : FeedStack (Breaking / Trending / Top / Verified)
 *   BOTTOM    : RundownTimeline (cold open → close)
 *
 * Reuses existing /api/show/latest, /api/crew/status, /api/delta/sensors/summary.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import '../../styles/studio.css';
import './studio-shell.css';

import ShowHeader from './ShowHeader';
import EpisodeOverviewCard from './EpisodeOverviewCard';
import PanelGrid from './PanelGrid';
import FeedStack from './FeedStack';
import RundownTimeline from './RundownTimeline';
import ProductionCrewStatusStrip from './ProductionCrewStatusStrip';

const TIER1_AGENTS = [
  { id: 'nova',  name: 'NOVA',  role: 'Weather / Environmental Anchor',  color: 'var(--neon-nova)'  },
  { id: 'orion', name: 'ORION', role: 'Space Weather / Physics Explainer', color: 'var(--neon-orion)' },
  { id: 'vega',  name: 'VEGA',  role: 'Critical Systems / Operational',   color: 'var(--neon-vega)'  },
  { id: 'atlas', name: 'ATLAS', role: 'Systems / Geopolitics / Markets',  color: 'var(--neon-atlas)' },
];

export default function StudioShow({ mode = 'show', onNavigate, onSwitchAgent }) {
  const [episode, setEpisode] = useState(null);
  const [crew, setCrew] = useState([]);
  const [focusedAgent, setFocusedAgent] = useState(null);
  const [sensorSummary, setSensorSummary] = useState(null);
  const [agentStates, setAgentStates] = useState({});

  const loadAll = useCallback(async () => {
    try {
      const [epRes, crewRes, sensorRes] = await Promise.all([
        api.get('/api/show/latest').catch(() => ({ data: null })),
        api.get('/api/crew/status').catch(() => ({ data: { crew: [] } })),
        api.get('/api/delta/sensors/summary').catch(() => ({ data: null })),
      ]);
      if (epRes.data && !epRes.data.error) setEpisode(epRes.data);
      setCrew(crewRes.data?.crew || []);
      setSensorSummary(sensorRes.data || null);
    } catch (e) {
      console.warn('[Show] loadAll partial failure', e);
    }
  }, []);

  const loadAgentStates = useCallback(async () => {
    // Fire in parallel but tolerate auth failures per-agent.
    const out = {};
    await Promise.all(TIER1_AGENTS.map(async (a) => {
      try {
        const res = await api.get(`/api/${a.id}/state`);
        out[a.id] = res.data;
      } catch { /* tier-1 routes are auth-protected; public shell still renders */ }
    }));
    setAgentStates(out);
  }, []);

  useEffect(() => {
    loadAll();
    loadAgentStates();
    const id = setInterval(() => { loadAll(); loadAgentStates(); }, 30000);
    return () => clearInterval(id);
  }, [loadAll, loadAgentStates]);

  const showStatus = episode?.monitoring_state || episode?.monitoringState || 'PREP';
  const segments = episode?.segments || episode?.transcript || [];
  const activeSegmentIdx = Math.max(0, segments.findIndex((s) => s.active) || 0);

  return (
    <div className="studio-shell" data-testid="studio-shell">
      <ShowHeader
        mode={mode}
        onNavigate={onNavigate}
        status={showStatus}
        episode={episode}
        sensorSummary={sensorSummary}
        nextSpeaker={segments[activeSegmentIdx + 1]?.speaker || segments[0]?.speaker || 'host'}
      />

      <div className="studio-body" data-testid="studio-body">
        {/* LEFT COLUMN */}
        <aside className="studio-col" data-testid="studio-col-left">
          <EpisodeOverviewCard episode={episode} />
          <div className="studio-section-head">
            <span>Panel Order</span>
            <span className="count">{TIER1_AGENTS.length}</span>
          </div>
          <div className="studio-kv-block">
            {TIER1_AGENTS.map((a, idx) => (
              <div key={a.id} className="studio-kv" data-testid={`panel-order-${a.id}`}>
                <span className="k" style={{ color: a.color }}>{String(idx + 1).padStart(2, '0')} · {a.name}</span>
                <span className="v" style={{ color: 'var(--studio-text-2)' }}>{a.role}</span>
              </div>
            ))}
          </div>
          <div className="studio-section-head">
            <span>Production Crew</span>
            <span className="count">{crew.length}</span>
          </div>
          <ProductionCrewStatusStrip crew={crew} />
        </aside>

        {/* CENTER */}
        <main className="studio-col" data-testid="studio-col-center">
          <div className="studio-section-head">
            <span>On-Air Panel</span>
            <span className="count">{focusedAgent ? `focused · ${focusedAgent.toUpperCase()}` : '4 panelists'}</span>
          </div>
          <PanelGrid
            agents={TIER1_AGENTS}
            states={agentStates}
            focusedAgent={focusedAgent}
            onFocus={setFocusedAgent}
            onSwitchAgent={onSwitchAgent}
          />
        </main>

        {/* RIGHT COLUMN */}
        <aside className="studio-col" data-testid="studio-col-right">
          <FeedStack episode={episode} crew={crew} />
        </aside>
      </div>

      <RundownTimeline segments={segments} activeIdx={activeSegmentIdx} />
    </div>
  );
}
